import type { CSSProperties, FC, ReactNode } from 'react'

import { useRef } from 'react'

import { cn } from '@/lib/utils'
import { useGsap } from '@/lib/gsap'
import { parallax, frameOpen } from '@/lib/motion'

interface ScrollFigureProps {
    src: string
    alt: string
    className?: string
    imageClassName?: string
    aspect?: string
    /* Percent of its own height the picture drifts against the scroll. */
    drift?: number
    /* Small caption printed under the frame in the mono role. */
    caption?: string
    /* Index chip in the top-left corner, as the reference marks its plates. */
    index?: string
    delay?: number
    priority?: boolean
    style?: CSSProperties
}

/* The site's default way of placing a photograph: the mask opens from all
   four edges as the frame arrives, the picture settles out of an over-scale
   at a slower rate than the mask, and it keeps drifting against the scroll
   afterwards so the composition never sits completely still. */
export const ScrollFigure: FC<ScrollFigureProps> = ({
    src,
    alt,
    className,
    imageClassName,
    aspect = 'aspect-[4/5]',
    drift = 6,
    caption,
    index,
    delay = 0,
    priority = false,
    style
}): ReactNode => {
    const scope = useRef<HTMLElement>(null)

    useGsap(() => {
        const root = scope.current

        if (!root) {
            return
        }

        const frame = root.querySelector<HTMLElement>('[data-figure-frame]')
        const image = root.querySelector<HTMLElement>('[data-figure-image]')

        if (!frame || !image) {
            return
        }

        frameOpen(frame, image, delay)

        if (drift !== 0) {
            parallax(image, { amount: drift, scrub: 0.7, trigger: frame })
        }
    }, scope, [src, drift, delay])

    return (
        <figure ref={scope} className={cn('group/fig relative', className)} style={style}>
            <div
                data-figure-frame
                className={cn(
                    'relative w-full overflow-hidden rounded-[var(--radius-frame)] bg-ink-soft',
                    aspect
                )}
            >
                <img
                    data-figure-image
                    src={src}
                    alt={alt}
                    loading={priority ? 'eager' : 'lazy'}
                    decoding='async'
                    className={cn(
                        'absolute inset-0 size-full scale-[1.1] object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover/fig:scale-[1.15]',
                        imageClassName
                    )}
                />

                {index
                    ? (
                        <span className='t-mono absolute left-3 top-3 z-10 flex h-6 items-center rounded-[var(--radius-chip)] bg-ink/55 px-2 text-paper backdrop-blur-sm'>
                            {index}
                        </span>
                    )
                    : null}
            </div>

            {caption
                ? (
                    <figcaption className='t-mono mt-3 text-current opacity-55'>
                        {caption}
                    </figcaption>
                )
                : null}
        </figure>
    )
}
